import { findCategory, allCategories } from '../../lib/vitrinCategories';
import { useMessageTemplates, fillTemplate, HESAP_TEMPLATES } from '../../lib/messageTemplates';
import { WhatsAppButton } from '../../components/hesap/WhatsAppButton';
import { useHesapBranding } from '../../store/hesapBrandingStore';
import { tl } from '../../lib/whatsapp';
import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect, useRef } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Wrench, Plus, Search, Trash2, Edit2, Printer, Image as ImageIcon,
  Phone, Monitor, Tablet, AlertCircle, Package, ExternalLink,
  Save, RefreshCw, Shield, ShieldAlert, ShieldCheck, Hash, Share2, Eye
} from 'lucide-react';

const BACKEND = RUNTIME_BACKEND_URL;

const DURUM_OPTIONS = [
  { value: 'beklemede', label: 'Beklemede', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
  { value: 'onarimda', label: 'Onarımda', color: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-400' },
  { value: 'parca_bekliyor', label: 'Parça Bekleniyor', color: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-400' },
  { value: 'tamamlandi', label: 'Tamamlandı', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400' },
  { value: 'teslim_edildi', label: 'Teslim Edildi', color: 'bg-slate-50 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
];

const CIHAZ_TURLERI = [
  { value: 'telefon', label: 'Telefon', icon: Phone },
  { value: 'tablet', label: 'Tablet', icon: Tablet },
  { value: 'bilgisayar', label: 'Masaüstü PC', icon: Monitor },
  { value: 'dizustu', label: 'Dizüstü', icon: Monitor },
  { value: 'televizyon', label: 'Televizyon', icon: Monitor },
  { value: 'beyaz_esya', label: 'Beyaz Eşya', icon: Package },
  { value: 'kucuk_ev_aletleri', label: 'Küçük Ev Aleti', icon: Package },
  { value: 'diger', label: 'Diğer Elektronik', icon: Package },
];

// Cihaz türüne göre gösterilecek alanlar (dinamik form)
const CIHAZ_ALANLARI = {
  telefon:            { imei: true,  seri_no: true,  sifre: true,  isletim_sistemi: true,  aksesuar: true,  garanti: true,  model_no: false },
  tablet:             { imei: true,  seri_no: true,  sifre: true,  isletim_sistemi: true,  aksesuar: true,  garanti: true,  model_no: false },
  bilgisayar:         { imei: false, seri_no: true,  sifre: true,  isletim_sistemi: true,  aksesuar: true,  garanti: true,  model_no: true  },
  dizustu:            { imei: false, seri_no: true,  sifre: true,  isletim_sistemi: true,  aksesuar: true,  garanti: true,  model_no: true  },
  televizyon:         { imei: false, seri_no: true,  sifre: false, isletim_sistemi: false, aksesuar: true,  garanti: true,  model_no: true  },
  beyaz_esya:         { imei: false, seri_no: true,  sifre: false, isletim_sistemi: false, aksesuar: false, garanti: true,  model_no: true  },
  kucuk_ev_aletleri:  { imei: false, seri_no: true,  sifre: false, isletim_sistemi: false, aksesuar: false, garanti: true,  model_no: false },
  diger:              { imei: false, seri_no: true,  sifre: false, isletim_sistemi: false, aksesuar: true,  garanti: true,  model_no: false },
};

const STOK_DURUMLARI = [
  { value: 'satista', label: 'Satışta', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'satildi', label: 'Satıldı', color: 'bg-slate-50 text-slate-600 border-slate-200' },
  { value: 'rezerve', label: 'Rezerve', color: 'bg-purple-50 text-purple-700 border-purple-200' },
];

// IMEI Luhn doğrulama
function validateIMEI(imei) {
  if (!imei || imei.length !== 15 || !/^\d{15}$/.test(imei)) return { valid: false, reason: 'IMEI 15 haneli rakamlardan oluşmalıdır' };
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    let d = parseInt(imei[i]);
    if (i % 2 !== 0) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  const checkDigit = (10 - (sum % 10)) % 10;
  if (checkDigit !== parseInt(imei[14])) return { valid: false, reason: 'IMEI kontrol hanesi hatalı - sahte olabilir!' };
  return { valid: true, reason: 'IMEI geçerli' };
}

// Input component
function FormInput({ label, required, icon: Icon, ...props }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />}
        <input {...props}
          className={`w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all outline-none ${Icon ? 'pl-10' : ''} ${props.className || ''}`}
        />
      </div>
    </div>
  );
}

export default function HesapTeknikServisPage() {
  const { token } = useHesapAuth();
  const [activeTab, setActiveTab] = useState('kayitlar');
  const [records, setRecords] = useState([]);
  const [stokRecords, setStokRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [filterDurum, setFilterDurum] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (activeTab === 'kayitlar' || activeTab === 'yeni') fetchRecords();
    if (activeTab === 'stok') fetchStok();
  }, [activeTab]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/hesap/teknik-servis`, { headers });
      if (res.ok) setRecords(await res.json());
    } catch {} finally { setLoading(false); }
  };

  const fetchStok = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/hesap/cihaz-stok`, { headers });
      if (res.ok) setStokRecords(await res.json());
    } catch {} finally { setLoading(false); }
  };

  const filteredRecords = records.filter(r => {
    if (filterDurum && r.durum !== filterDurum) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (r.musteri_adi?.toLowerCase().includes(s) || r.marka?.toLowerCase().includes(s) ||
              r.model?.toLowerCase().includes(s) || r.imei?.includes(s) || r.id?.includes(s));
    }
    return true;
  });

  const tabs = [
    { id: 'kayitlar', label: 'Kayıtlar', count: records.length },
    { id: 'yeni', label: 'Yeni Kayıt' },
    { id: 'imei', label: 'IMEI Sorgulama' },
    { id: 'imei-kayit', label: 'Kayıtlı IMEI\'ler' },
  ];

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="teknik-servis-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg shadow-emerald-200">
            <Wrench className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Teknik Servis</h1>
            <p className="text-xs text-slate-400">Cihaz takip, onarım ve stok yönetimi</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100/80 p-1 rounded-2xl overflow-x-auto" data-testid="teknik-tabs">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); if (tab.id === 'yeni') setEditRecord(null); }}
            className={`relative px-5 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-white text-emerald-700 shadow-sm shadow-emerald-100'
                : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
            }`} data-testid={`tab-${tab.id}`}>
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                activeTab === tab.id ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
              }`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'kayitlar' && (
        <KayitlarTab records={filteredRecords} loading={loading}
          filterDurum={filterDurum} setFilterDurum={setFilterDurum}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          onEdit={r => { setEditRecord(r); setActiveTab('yeni'); }}
          onDelete={async id => { if (!window.confirm('Silmek istediğinize emin misiniz?')) return; await fetch(`${BACKEND}/api/hesap/teknik-servis/${id}`, { method: 'DELETE', headers }); fetchRecords(); toast.success('Silindi'); }}
          onPrint={r => window.open(`${BACKEND}/api/hesap/teknik-servis/${r.id}/fis`, '_blank')}
          onStatusChange={async (id, durum) => { await fetch(`${BACKEND}/api/hesap/teknik-servis/${id}`, { method: 'PUT', headers, body: JSON.stringify({ durum }) }); fetchRecords(); toast.success('Durum güncellendi'); }}
          token={token} />
      )}
      {activeTab === 'yeni' && (
        <YeniKayitTab record={editRecord} token={token} headers={headers}
          onSave={async data => {
            const url = editRecord ? `${BACKEND}/api/hesap/teknik-servis/${editRecord.id}` : `${BACKEND}/api/hesap/teknik-servis`;
            try {
              const res = await fetch(url, { method: editRecord ? 'PUT' : 'POST', headers, body: JSON.stringify(data) });
              if (res.ok) { toast.success(editRecord ? 'Güncellendi' : 'Kayıt oluşturuldu'); setEditRecord(null); setActiveTab('kayitlar'); fetchRecords(); }
              else { const d = await res.json().catch(() => ({})); toast.error(d.detail || `Hata (${res.status})`); }
            } catch (err) {
              toast.error('Bağlantı hatası: ' + err.message);
            }
          }}
          onCancel={() => { setEditRecord(null); setActiveTab('kayitlar'); }} />
      )}
      {activeTab === 'imei' && <ImeiTab token={token} />}
      {activeTab === 'imei-kayit' && <ImeiKayitTab token={token} headers={headers} />}
    </div>
  );
}

// ==================== KAYITLAR ====================
function KayitlarTab({ records, loading, filterDurum, setFilterDurum, searchTerm, setSearchTerm, onEdit, onDelete, onPrint, onStatusChange }) {
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="text" placeholder="Müşteri, marka, IMEI ara..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none" data-testid="search-input" />
        </div>
        <select value={filterDurum} onChange={e => setFilterDurum(e.target.value)}
          className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:border-emerald-400 outline-none" data-testid="filter-durum">
          <option value="">Tüm Durumlar</option>
          {DURUM_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </div>

      {loading ? <div className="text-center py-16 text-slate-400">Yükleniyor...</div> :
       records.length === 0 ? (
        <div className="text-center py-16">
          <Wrench className="h-12 w-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-400">Kayıt bulunamadı</p>
        </div>
      ) : (
        <div className="space-y-3">
          {records.map(r => <RecordCard key={r.id} r={r} onEdit={onEdit} onDelete={onDelete} onPrint={onPrint} onStatusChange={onStatusChange} />)}
        </div>
      )}
    </div>
  );
}

const servisMessage = (r, brand, get) => {
  const vars = {
    musteri: r.musteri_adi, servis_no: r.id.slice(0, 8).toUpperCase(), cihaz: `${r.marka || ''} ${r.model || ''}`.trim(), firma: brand,
    ucret: (r.alinan_ucret || r.tahmini_ucret) ? `Ücret: ${tl(r.alinan_ucret || r.tahmini_ucret)}.` : '',
    durum: (DURUM_OPTIONS.find(x => x.value === r.durum) || DURUM_OPTIONS[0]).label,
  };
  return fillTemplate(get(r.durum === 'tamamlandi' ? 'servis_hazir' : 'servis_durum'), vars);
};

function RecordCard({ r, onEdit, onDelete, onPrint, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const brand = useHesapBranding(st => st.title);
  const tpl = useMessageTemplates('/api/hesap', useHesapAuth(st => st.token), HESAP_TEMPLATES);
  const d = DURUM_OPTIONS.find(x => x.value === r.durum) || DURUM_OPTIONS[0];
  const c = CIHAZ_TURLERI.find(x => x.value === r.cihaz_turu) || CIHAZ_TURLERI[0];
  const CIcon = c.icon;

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all" data-testid={`servis-card-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className="p-2.5 bg-slate-50 rounded-xl flex-shrink-0">
          <CIcon className="h-5 w-5 text-slate-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-800">{r.marka} {r.model}</span>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border ${d.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${d.dot}`} /> {d.label}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
            <span className="font-medium">{r.musteri_adi}</span>
            {r.musteri_tel && <span className="text-slate-400">{r.musteri_tel}</span>}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
            {r.imei && <span className="font-mono">IMEI: {r.imei}</span>}
            <span>#{r.id.slice(0,8).toUpperCase()}</span>
            <span>{r.created_at?.slice(0,10)}</span>
          </div>
          {r.ariza_aciklamasi && <p className="text-xs text-slate-500 mt-1.5 bg-slate-50 rounded-lg px-3 py-1.5">{r.ariza_aciklamasi}</p>}
          {(r.tahmini_ucret > 0 || r.alinan_ucret > 0) && (
            <div className="flex gap-4 mt-2 text-xs">
              {r.tahmini_ucret > 0 && <span className="text-slate-500">Tahmini: <b className="text-slate-700">{r.tahmini_ucret.toLocaleString('tr-TR')} TL</b></span>}
              {r.alinan_ucret > 0 && <span className="text-emerald-600">Alınan: <b>{r.alinan_ucret.toLocaleString('tr-TR')} TL</b></span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {r.musteri_tel && (
            <WhatsAppButton compact phone={r.musteri_tel} text={() => servisMessage(r, brand, tpl.get)}
              label={r.durum === 'tamamlandi' ? 'Cihazınız hazır mesajı' : 'Durum bilgisi gönder'} testId={`servis-wa-${r.id}`} className="mr-1" />
          )}
          <div className="relative">
            <button onClick={() => setOpen(!open)} className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors" title="Durum">
              <RefreshCw className="h-4 w-4" />
            </button>
            {open && (
              <div className="absolute right-0 top-10 bg-white border border-slate-100 rounded-2xl shadow-xl z-20 py-2 w-48">
                {DURUM_OPTIONS.map(s => (
                  <button key={s.value} onClick={() => { onStatusChange(r.id, s.value); setOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2.5 transition-colors ${r.durum === s.value ? 'font-bold text-emerald-600 bg-emerald-50' : 'text-slate-600'}`}>
                    <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => onPrint(r)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors" title="Fiş"><Printer className="h-4 w-4" /></button>
          <button onClick={() => onEdit(r)} className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors" title="Düzenle"><Edit2 className="h-4 w-4" /></button>
          <button onClick={() => onDelete(r.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors" title="Sil"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}

// ==================== YENİ KAYIT ====================
function YeniKayitTab({ record, token, headers, onSave, onCancel }) {
  const [form, setForm] = useState(record || {
    musteri_id: '', musteri_adi: '', musteri_tel: '',
    cihaz_turu: 'telefon', marka: '', model: '', model_no: '',
    imei: '', seri_no: '', sifre: '', isletim_sistemi: '', aksesuar: '',
    ariza_aciklamasi: '', tahmini_ucret: 0, alinan_ucret: 0, garanti: false, notlar: '',
    durum: 'beklemede'
  });
  const [uploading, setUploading] = useState(false);
  const [musteriler, setMusteriler] = useState([]);
  const [musteriSearch, setMusteriSearch] = useState('');
  const [showMusteriList, setShowMusteriList] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Cihaz türüne göre hangi alanlar gösterilecek
  const alanlar = CIHAZ_ALANLARI[form.cihaz_turu] || CIHAZ_ALANLARI.diger;

  // Müşteri listesi (mevcut müşterileri seçebilmek için)
  useEffect(() => {
    fetch(`${BACKEND}/api/hesap/customers`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(setMusteriler)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredMusteriler = musteriler.filter(m => {
    if (!musteriSearch) return false;
    const s = musteriSearch.toLowerCase();
    return (m.full_name || '').toLowerCase().includes(s) || (m.phone || '').includes(musteriSearch);
  }).slice(0, 8);

  const selectMusteri = (m) => {
    set('musteri_id', m.id);
    set('musteri_adi', m.full_name);
    set('musteri_tel', m.phone || '');
    setMusteriSearch('');
    setShowMusteriList(false);
    toast.success(`Müşteri seçildi: ${m.full_name}`);
  };

  const clearMusteri = () => {
    set('musteri_id', '');
    set('musteri_adi', '');
    set('musteri_tel', '');
  };

  const uploadPhoto = async (file) => {
    if (!record?.id) { toast.error('Önce kaydedin, sonra resim ekleyin'); return; }
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await fetch(`${BACKEND}/api/hesap/teknik-servis/${record.id}/resim`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      if (res.ok) { const d = await res.json(); set('resimler', [...(form.resimler || []), d.filename]); toast.success('Resim eklendi'); }
    } catch { toast.error('Yüklenemedi'); } finally { setUploading(false); }
  };

  return (
    <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" data-testid="servis-form">
      {/* Form header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
        <h2 className="text-lg font-bold text-white">{record ? 'Kaydı Düzenle' : 'Yeni Teknik Servis Kaydı'}</h2>
        <p className="text-emerald-100 text-xs mt-0.5">Cihaz ve müşteri bilgilerini girin</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Müşteri */}
        <div>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <div className="w-6 h-6 bg-emerald-100 rounded-lg flex items-center justify-center"><Phone className="h-3 w-3 text-emerald-600" /></div>
            Müşteri Bilgileri
            {form.musteri_id && (
              <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium normal-case">
                ✓ Kayıtlı müşteri seçildi
              </span>
            )}
          </h3>
          {/* Kayıtlı müşteri seçim kutusu */}
          <div className="mb-3 relative">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Kayıtlı Müşteri Seç (opsiyonel)</label>
            <div className="relative">
              <input
                type="text"
                value={musteriSearch}
                onChange={e => { setMusteriSearch(e.target.value); setShowMusteriList(true); }}
                onFocus={() => setShowMusteriList(true)}
                placeholder="Ad veya telefon ile müşteri ara..."
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:border-emerald-400 outline-none pl-9"
                data-testid="musteri-search-input"
              />
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              {form.musteri_id && (
                <button type="button" onClick={clearMusteri} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-red-500 hover:underline">Temizle</button>
              )}
            </div>
            {showMusteriList && filteredMusteriler.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {filteredMusteriler.map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => selectMusteri(m)}
                    className="w-full text-left px-4 py-2 hover:bg-emerald-50 text-sm border-b border-slate-100 last:border-0"
                    data-testid={`musteri-option-${m.id}`}
                  >
                    <div className="font-medium">{m.full_name}</div>
                    {m.phone && <div className="text-xs text-slate-500">{m.phone}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Müşteri Adı" required value={form.musteri_adi} onChange={e => set('musteri_adi', e.target.value)} placeholder="Ad Soyad" data-testid="input-musteri-adi" />
            <FormInput label="Telefon" icon={Phone} type="tel" value={form.musteri_tel} onChange={e => set('musteri_tel', e.target.value)} placeholder="05XX XXX XXXX" />
          </div>
        </div>

        {/* Cihaz */}
        <div>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center"><Monitor className="h-3 w-3 text-blue-600" /></div>
            Cihaz Bilgileri
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Cihaz Türü</label>
              <select value={form.cihaz_turu} onChange={e => set('cihaz_turu', e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:border-emerald-400 outline-none" data-testid="input-cihaz-turu">
                {CIHAZ_TURLERI.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <FormInput label="Marka" required value={form.marka} onChange={e => set('marka', e.target.value)} placeholder="Samsung, Apple, LG..." data-testid="input-marka" />
            <FormInput label="Model" value={form.model} onChange={e => set('model', e.target.value)} placeholder={form.cihaz_turu === 'telefon' ? 'Galaxy S23...' : 'Model...'} />
            {alanlar.imei && (
              <FormInput label="IMEI" icon={Hash} value={form.imei} onChange={e => set('imei', e.target.value)} placeholder="15 haneli" maxLength={15} />
            )}
            {alanlar.model_no && (
              <FormInput label="Model No" value={form.model_no || ''} onChange={e => set('model_no', e.target.value)} placeholder="Ör: LC-32ABC" />
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {alanlar.seri_no && (
              <FormInput label="Seri No" value={form.seri_no} onChange={e => set('seri_no', e.target.value)} placeholder="Cihaz seri numarası" />
            )}
            {alanlar.sifre && (
              <FormInput label="Ekran Kilidi / Şifre" value={form.sifre || ''} onChange={e => set('sifre', e.target.value)} placeholder="1234, desen vs." />
            )}
            {alanlar.isletim_sistemi && (
              <FormInput label="İşletim Sistemi" value={form.isletim_sistemi || ''} onChange={e => set('isletim_sistemi', e.target.value)} placeholder={form.cihaz_turu === 'telefon' ? 'Android 14 / iOS 17' : 'Windows 11 / macOS'} />
            )}
            {alanlar.aksesuar && (
              <FormInput label="Teslim Alınan Aksesuarlar" value={form.aksesuar || ''} onChange={e => set('aksesuar', e.target.value)} placeholder="Şarj cihazı, kılıf..." />
            )}
            {alanlar.garanti && (
              <div className="flex items-end">
                <label className="flex items-center gap-3 cursor-pointer bg-slate-50 rounded-xl px-4 py-2.5 w-full border border-slate-200 hover:border-emerald-300 transition-colors">
                  <input type="checkbox" checked={form.garanti} onChange={e => set('garanti', e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-400" />
                  <span className="text-sm text-slate-600 font-medium">Garantili Cihaz</span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Arıza */}
        <div>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <div className="w-6 h-6 bg-red-100 rounded-lg flex items-center justify-center"><AlertCircle className="h-3 w-3 text-red-600" /></div>
            Arıza ve Ücret
          </h3>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Arıza Açıklaması</label>
            <textarea value={form.ariza_aciklamasi} onChange={e => set('ariza_aciklamasi', e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none resize-none"
              rows={3} placeholder="Cihazın arızasını detaylı yazın..." data-testid="input-ariza" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <FormInput label="Tahmini Ücret (TL)" type="number" value={form.tahmini_ucret} onChange={e => set('tahmini_ucret', parseFloat(e.target.value) || 0)} min={0} step={0.01} />
            <FormInput label="Alınan Ücret (TL)" type="number" value={form.alinan_ucret} onChange={e => set('alinan_ucret', parseFloat(e.target.value) || 0)} min={0} step={0.01} />
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Notlar</label>
            <textarea value={form.notlar} onChange={e => set('notlar', e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-white focus:border-emerald-400 outline-none resize-none" rows={2} placeholder="Ek notlar..." />
          </div>
        </div>

        {/* Resimler */}
        <div>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center"><ImageIcon className="h-3 w-3 text-purple-600" /></div>
            Cihaz Fotoğrafları
          </h3>
          <div className="flex flex-wrap gap-3">
            {(form.resimler || []).map((img, i) => (
              <div key={i} className="w-24 h-24 border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <img src={`${BACKEND}/api/hesap/teknik-servis/resim/${img}`} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || !record?.id}
              className="w-24 h-24 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:text-emerald-500 hover:border-emerald-400 transition-all disabled:opacity-40">
              {uploading ? <RefreshCw className="h-6 w-6 animate-spin" /> : <ImageIcon className="h-6 w-6" />}
              <span className="text-[10px] mt-1">{record?.id ? 'Resim Ekle' : 'Önce Kaydet'}</span>
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadPhoto(e.target.files[0]); }} />
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-4 border-t border-slate-100">
          <Button onClick={() => { if (!form.musteri_adi || !form.marka) { toast.error('Müşteri adı ve marka zorunlu'); return; } onSave(form); }}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg shadow-emerald-200 px-8" data-testid="save-servis-btn">
            <Save className="h-4 w-4 mr-2" /> {record ? 'Güncelle' : 'Kaydet'}
          </Button>
          <Button variant="ghost" onClick={onCancel} className="text-slate-500">İptal</Button>
        </div>
      </div>
    </div>
  );
}

// ==================== STOK ====================
export const useStokRecords = (headers) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    try {
      const res = await fetch(`${BACKEND}/api/hesap/cihaz-stok`, { headers });
      if (res.ok) setRecords(await res.json());
    } catch { /* noop */ } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);
  return { records, loading, refresh };
};

export function StokTab({ records, loading, token, headers, onRefresh, customCategories = [] }) {
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const handleSave = async (data) => {
    const url = editItem ? `${BACKEND}/api/hesap/cihaz-stok/${editItem.id}` : `${BACKEND}/api/hesap/cihaz-stok`;
    const res = await fetch(url, { method: editItem ? 'PUT' : 'POST', headers, body: JSON.stringify(data) });
    if (res.ok) { toast.success(editItem ? 'Güncellendi' : 'Eklendi'); setShowForm(false); setEditItem(null); onRefresh(); }
    else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Hata'); }
  };

  if (showForm) {
    return (
      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-4">
          <h2 className="text-lg font-bold text-white">{editItem ? 'Cihazı Düzenle' : 'Yeni Cihaz Ekle'}</h2>
        </div>
        <div className="p-6 space-y-4">
          <StokFormInner item={editItem} onSave={handleSave} onCancel={() => { setShowForm(false); setEditItem(null); }} token={token} customCategories={customCategories} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">{records.length} cihaz</p>
        <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg shadow-emerald-200 rounded-xl"
          onClick={() => { setEditItem(null); setShowForm(true); }} data-testid="add-stok-btn">
          <Plus className="h-4 w-4 mr-1" /> Yeni Cihaz
        </Button>
      </div>

      {loading ? <div className="text-center py-16 text-slate-400">Yükleniyor...</div> :
       records.length === 0 ? (
        <div className="text-center py-16">
          <Package className="h-12 w-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-400">Stokta cihaz yok</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {records.map(r => {
            const s = STOK_DURUMLARI.find(x => x.value === r.stok_durumu) || STOK_DURUMLARI[0];
            const c = { ...(CIHAZ_TURLERI.find(x => x.value === r.cihaz_turu) || CIHAZ_TURLERI[CIHAZ_TURLERI.length - 1]), label: findCategory(r.cihaz_turu, customCategories).label };
            const kar = r.satis_fiyati - r.alis_fiyati;
            return (
              <div key={r.id} className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all" data-testid={`stok-card-${r.id}`}>
                <div className="h-36 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center relative">
                  {r.resimler?.length > 0 ? (
                    <img src={`${BACKEND}/api/hesap/teknik-servis/resim/${r.resimler[0]}`} alt="" className="w-full h-full object-cover" />
                  ) : <c.icon className="h-14 w-14 text-slate-200" />}
                  <span className={`absolute top-3 right-3 px-2.5 py-1 rounded-lg text-[10px] font-bold border ${s.color}`}>{s.label}</span>
                  <span className="absolute top-3 left-3 px-2.5 py-1 bg-black/70 text-white rounded-lg text-[10px] font-bold backdrop-blur-sm">
                    {r.durum === 'sifir' ? 'Sıfır' : '2.El'}
                  </span>
                </div>
                <div className="p-4">
                  <div className="font-bold text-slate-800">{r.marka} {r.model}</div>
                  {r.imei && <div className="text-[10px] text-slate-400 mt-0.5 font-mono">IMEI: {r.imei}</div>}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <div className="text-xs text-slate-400">Alış: <b className="text-slate-600">{r.alis_fiyati?.toLocaleString('tr-TR')}</b></div>
                    <div className="text-sm font-bold text-emerald-600">{r.satis_fiyati?.toLocaleString('tr-TR')} TL</div>
                  </div>
                  {kar > 0 && <div className="text-[10px] text-emerald-500 mt-1 text-right">Kâr: +{kar.toLocaleString('tr-TR')} TL</div>}
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100">
                    <button onClick={() => { setEditItem(r); setShowForm(true); }} className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Düzenle"><Edit2 className="h-3.5 w-3.5" /></button>
                    <button onClick={async () => { if (!window.confirm('Sil?')) return; await fetch(`${BACKEND}/api/hesap/cihaz-stok/${r.id}`, { method: 'DELETE', headers }); onRefresh(); }}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Sil"><Trash2 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => {
                      const shareUrl = `${window.location.origin}/cihaz/${r.id}`;
                      const text = `${r.marka} ${r.model} - ${r.satis_fiyati?.toLocaleString('tr-TR')} TL\n\n${r.ozellikler || ''}\n\nDetay: ${shareUrl}`;
                      if (navigator.share) {
                        navigator.share({ title: `${r.marka} ${r.model}`, text, url: shareUrl }).catch(() => {});
                      } else {
                        navigator.clipboard.writeText(shareUrl).then(() => toast.success('Paylaşım linki kopyalandı'));
                      }
                    }}
                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Paylaş"
                      data-testid={`stok-share-${r.id}`}>
                      <Share2 className="h-3.5 w-3.5" />
                    </button>
                    <a href={`/cihaz/${r.id}`} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg" title="Önizle">
                      <Eye className="h-3.5 w-3.5" />
                    </a>
                    <div className="flex-1" />
                    <select value={r.stok_durumu}
                      onChange={async e => { await fetch(`${BACKEND}/api/hesap/cihaz-stok/${r.id}`, { method: 'PUT', headers, body: JSON.stringify({ stok_durumu: e.target.value }) }); onRefresh(); }}
                      className="text-[10px] border border-slate-200 rounded-lg px-2 py-1 bg-white">
                      {STOK_DURUMLARI.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StokFormInner({ item, onSave, onCancel, token, customCategories = [] }) {
  const [f, setF] = useState(item || { cihaz_turu: 'telefon', durum: 'ikinci_el', marka: '', model: '', imei: '', seri_no: '', alis_fiyati: 0, satis_fiyati: 0, aciklama: '', ozellikler: '', resimler: [], ekstra: {} });
  const cat = findCategory(f.cihaz_turu, customCategories);
  const getV = (k) => (k === 'imei' || k === 'seri_no' ? f[k] : (f.ekstra || {})[k]) ?? '';
  const setV = (k, v) => (k === 'imei' || k === 'seri_no' ? setF(p => ({ ...p, [k]: v })) : setF(p => ({ ...p, ekstra: { ...(p.ekstra || {}), [k]: v } })));
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));

  const uploadImage = async (file) => {
    if (!item?.id) { toast.error('Önce kaydet, sonra resim ekleyebilirsin'); return; }
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await fetch(`${BACKEND}/api/hesap/cihaz-stok/${item.id}/resim`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      if (res.ok) {
        const d = await res.json();
        s('resimler', [...(f.resimler || []), d.filename]);
        toast.success('Resim eklendi');
      } else toast.error('Yüklenemedi');
    } catch { toast.error('Bağlantı hatası'); } finally { setUploading(false); }
  };

  const deleteImage = async (filename) => {
    if (!item?.id) return;
    const res = await fetch(`${BACKEND}/api/hesap/cihaz-stok/${item.id}/resim/${filename}`, {
      method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      s('resimler', (f.resimler || []).filter(r => r !== filename));
      toast.success('Silindi');
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Kategori</label>
          <select value={f.cihaz_turu} onChange={e => s('cihaz_turu', e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white" data-testid="vitrin-category-select">
            {allCategories(customCategories).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Durum</label>
          <select value={f.durum} onChange={e => s('durum', e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white">
            <option value="sifir">Sıfır</option><option value="ikinci_el">2. El</option>
          </select>
        </div>
        <FormInput label="Marka" required value={f.marka} onChange={e => s('marka', e.target.value)} placeholder="Samsung..." />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <FormInput label="Model / Ürün Adı" value={f.model} onChange={e => s('model', e.target.value)} />
      </div>
      {cat.fields.length > 0 && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4" data-testid="vitrin-category-fields">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-3">{cat.label} Özellikleri</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {cat.fields.map(fd => (
              <div key={fd.key}>
                <label className="block text-xs font-semibold text-slate-500 mb-1">{fd.label}</label>
                {fd.type === 'select' ? (
                  <select value={getV(fd.key)} onChange={e => setV(fd.key, e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white">
                    <option value="">Seçin</option>
                    {fd.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={fd.type === 'number' ? 'number' : 'text'} value={getV(fd.key)} onChange={e => setV(fd.key, e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <FormInput label="Alış Fiyatı (TL)" type="number" value={f.alis_fiyati} onChange={e => s('alis_fiyati', parseFloat(e.target.value) || 0)} min={0} />
        <FormInput label="Satış Fiyatı (TL)" type="number" value={f.satis_fiyati} onChange={e => s('satis_fiyati', parseFloat(e.target.value) || 0)} min={0} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Özellikler (müşteriye gösterilecek)</label>
        <textarea value={f.ozellikler || ''} onChange={e => s('ozellikler', e.target.value)}
          placeholder="Örn: 128 GB, 8 GB RAM, Mavi, Temiz kutu..."
          rows={2}
          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-emerald-400 outline-none resize-none" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Açıklama / Not</label>
        <textarea value={f.aciklama || ''} onChange={e => s('aciklama', e.target.value)}
          placeholder="İç not veya ek bilgi..."
          rows={2}
          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-emerald-400 outline-none resize-none" />
      </div>

      {/* Cihaz Fotoğrafları */}
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide flex items-center gap-2">
          <ImageIcon className="h-3.5 w-3.5" /> Cihaz Fotoğrafları
        </label>
        <div className="flex flex-wrap gap-3 items-start">
          {(f.resimler || []).map((img, i) => (
            <div key={i} className="relative group w-24 h-24 border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <img src={`${BACKEND}/api/hesap/teknik-servis/resim/${img}`} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => deleteImage(img)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || !item?.id}
            className="w-24 h-24 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:text-emerald-500 hover:border-emerald-400 transition-all disabled:opacity-40">
            {uploading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-6 w-6" />}
            <span className="text-[10px] mt-1">{item?.id ? 'Galeri' : 'Önce Kaydet'}</span>
          </button>
          <button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading || !item?.id}
            className="w-24 h-24 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:text-blue-500 hover:border-blue-400 transition-all disabled:opacity-40">
            <Phone className="h-6 w-6" />
            <span className="text-[10px] mt-1">{item?.id ? 'Kamera' : 'Önce Kaydet'}</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={e => { if (e.target.files[0]) uploadImage(e.target.files[0]); e.target.value=''; }} />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={e => { if (e.target.files[0]) uploadImage(e.target.files[0]); e.target.value=''; }} />
        </div>
        {!item?.id && <p className="text-[11px] text-amber-600 mt-1">Önce kaydet, sonra resim ekleyebilirsin</p>}
      </div>

      <div className="flex gap-3 pt-4">
        <Button onClick={() => { if (!f.marka) { toast.error('Marka zorunlu'); return; } onSave(f); }}
          className="bg-gradient-to-r from-blue-500 to-indigo-500 shadow-lg shadow-blue-200 px-8"><Save className="h-4 w-4 mr-2" /> Kaydet</Button>
        <Button variant="ghost" onClick={onCancel}>İptal</Button>
      </div>
    </>
  );
}

// ==================== IMEI SORGULAMA ====================
function ImeiTab({ token }) {
  const [imei, setImei] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Manuel kayıt formu
  const [showSave, setShowSave] = useState(false);
  const [saveForm, setSaveForm] = useState({
    imei: '', isim: '', not_alan: '',
    durum: '', kaynak: '', marka_model: '', valid: true
  });

  const handleCheck = async () => {
    if (imei.length !== 15) { toast.error('IMEI 15 haneli olmalı'); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${BACKEND}/api/hesap/imei-sorgula/${imei}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setResult(data);
      // Kayıt formunu önceden doldur
      setSaveForm(p => ({
        ...p,
        imei: data.imei || imei,
        durum: data.durum || '',
        kaynak: data.kaynak || '',
        marka_model: data.marka_model || '',
        valid: !!data.valid
      }));
    } catch (err) {
      setResult({ valid: false, durum: 'HATA', reason: 'Sorgulama yapılamadı: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSubmit = async () => {
    if (!saveForm.isim.trim()) { toast.error('İsim zorunlu'); return; }
    try {
      const res = await fetch(`${BACKEND}/api/hesap/imei-kayitlari`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...saveForm,
          sorgu_tarihi: new Date().toLocaleDateString('tr-TR')
        })
      });
      if (res.ok) {
        toast.success('IMEI kaydedildi - "Kayıtlı IMEI\'ler" sekmesinde görebilirsiniz');
        setShowSave(false);
        setSaveForm({ imei: '', isim: '', not_alan: '', durum: '', kaynak: '', marka_model: '', valid: true });
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Kaydedilemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const setSF = (k, v) => setSaveForm(p => ({ ...p, [k]: v }));

  return (
    <div className="max-w-3xl mx-auto" data-testid="imei-tab">
      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-5 text-center">
          <Shield className="h-10 w-10 text-white/90 mx-auto mb-2" />
          <h2 className="text-lg font-bold text-white">IMEI Sorgulama</h2>
          <p className="text-indigo-100 text-xs mt-1">Luhn doğrulama + TAC veritabanı ile cihaz tanıma</p>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-xs text-slate-400 text-center">
            IMEI öğrenmek için cihazda <b className="text-slate-600">*#06#</b> tuşlayın
          </p>

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type="text" value={imei}
                onChange={e => { setImei(e.target.value.replace(/\D/g, '').slice(0, 15)); setResult(null); }}
                placeholder="IMEI numarası (15 hane)"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm font-mono tracking-wider focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none"
                maxLength={15} data-testid="imei-input" />
            </div>
            <Button onClick={handleCheck} disabled={imei.length < 15 || loading}
              className="bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-indigo-200 px-6 rounded-xl"
              data-testid="imei-check-btn">
              {loading ? 'Sorgulanıyor...' : (<><Search className="h-4 w-4 mr-1" /> Sorgula</>)}
            </Button>
          </div>

          {/* Sonuç - e-Devlet tarzı tablo */}
          {result && (
            <div className="bg-white border-2 border-slate-100 rounded-2xl overflow-hidden" data-testid="imei-result">
              <div className={`px-5 py-3 flex items-center justify-between ${result.valid ? 'bg-emerald-50' : 'bg-red-50'}`}>
                <div className="flex items-center gap-2">
                  {result.valid ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 text-red-600" />}
                  <span className={`font-bold text-sm ${result.valid ? 'text-emerald-700' : 'text-red-700'}`}>
                    Sorgu Sonucu
                  </span>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                <ResultRow label="IMEI" value={result.imei || imei} mono />
                <ResultRow label="Durum" value={result.durum || '-'} highlight={result.valid ? 'green' : 'red'} />
                <ResultRow label="Kaynak" value={result.kaynak || '-'} />
                <ResultRow label="Sorgu Tarihi" value={result.sorgu_tarihi || new Date().toLocaleDateString('tr-TR')} />
                <ResultRow label="Marka / Model" value={result.marka_model || '-'} />
              </div>
              {result.reason && (
                <div className={`m-4 px-4 py-2.5 rounded-xl text-xs ${result.valid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {result.reason}
                </div>
              )}
            </div>
          )}

          {/* Kaydet butonu */}
          {result && (
            <Button onClick={() => setShowSave(s => !s)}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg shadow-emerald-200 py-3 rounded-xl"
              data-testid="show-save-form-btn">
              <Save className="h-4 w-4 mr-2" /> {showSave ? 'Formu Kapat' : 'Bu Sonucu Kaydet'}
            </Button>
          )}

          {/* Manuel kayıt formu */}
          {showSave && result && (
            <div className="bg-white border-2 border-emerald-100 rounded-2xl overflow-hidden" data-testid="manual-save-form">
              <div className="bg-emerald-50 px-5 py-3 flex items-center gap-2">
                <Save className="h-5 w-5 text-emerald-600" />
                <h3 className="font-bold text-emerald-700 text-sm">Kayıt Bilgileri</h3>
              </div>
              <div className="p-5 space-y-4">
                <FormInput label="İsim / Etiket" required
                  value={saveForm.isim}
                  onChange={e => setSF('isim', e.target.value)}
                  placeholder="Örn: Ahmet'in iPhone'u" autoFocus
                  data-testid="save-isim-input" />
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Not (opsiyonel)</label>
                  <textarea value={saveForm.not_alan} onChange={e => setSF('not_alan', e.target.value)}
                    placeholder="Müşteri bilgisi, alış tarihi vb."
                    rows={2}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-emerald-400 outline-none resize-none" />
                </div>
                <div className="flex gap-3 pt-2 border-t border-slate-100">
                  <Button onClick={handleSaveSubmit}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg shadow-emerald-200 px-8"
                    data-testid="save-imei-btn">
                    <Save className="h-4 w-4 mr-2" /> Kaydet
                  </Button>
                  <Button variant="ghost" onClick={() => setShowSave(false)} className="text-slate-500">
                    İptal
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* e-Devlet linki (opsiyonel) */}
          <div className="text-center pt-3 border-t border-slate-100">
            <p className="text-[11px] text-slate-400 mb-2">Resmi BTK kaydı için:</p>
            <a href="https://www.turkiye.gov.tr/imei-sorgulama"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-xl font-medium transition-all"
              data-testid="imei-edevlet-link">
              <ExternalLink className="h-3 w-3" /> e-Devlet'e git
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultRow({ label, value, mono, highlight }) {
  const colorClass = highlight === 'green' ? 'text-emerald-600 font-bold' : highlight === 'red' ? 'text-red-600 font-bold' : 'text-slate-700';
  return (
    <div className="flex items-center px-5 py-3 hover:bg-slate-50/50">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-32 sm:w-40 flex-shrink-0">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''} ${colorClass} flex-1`}>{value}</div>
    </div>
  );
}

// ==================== KAYITLI IMEI'LER ====================
function ImeiKayitTab({ token, headers }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null);
  const [editIsim, setEditIsim] = useState('');
  const [editNot, setEditNot] = useState('');

  const fetchKayitlar = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/hesap/imei-kayitlari`, { headers });
      if (res.ok) setRecords(await res.json());
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchKayitlar(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Bu IMEI kaydını silmek istiyor musunuz?')) return;
    const res = await fetch(`${BACKEND}/api/hesap/imei-kayitlari/${id}`, { method: 'DELETE', headers });
    if (res.ok) { toast.success('Silindi'); fetchKayitlar(); }
    else toast.error('Silinemedi');
  };

  const startEdit = (r) => { setEditId(r.id); setEditIsim(r.isim || ''); setEditNot(r.not_alan || ''); };
  const cancelEdit = () => { setEditId(null); setEditIsim(''); setEditNot(''); };
  const saveEdit = async (id) => {
    const res = await fetch(`${BACKEND}/api/hesap/imei-kayitlari/${id}`, {
      method: 'PUT', headers, body: JSON.stringify({ isim: editIsim, not_alan: editNot })
    });
    if (res.ok) { toast.success('Güncellendi'); cancelEdit(); fetchKayitlar(); }
    else toast.error('Güncellenemedi');
  };

  const filtered = records.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.isim?.toLowerCase().includes(s) || r.imei?.includes(s) || r.marka_model?.toLowerCase().includes(s));
  });

  return (
    <div data-testid="imei-kayit-tab">
      <div className="flex flex-wrap gap-2 mb-4 items-center justify-between">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="text" placeholder="İsim, IMEI veya marka ara..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none" />
        </div>
        <p className="text-sm text-slate-500">{filtered.length} kayıt</p>
      </div>

      {loading ? <div className="text-center py-16 text-slate-400">Yükleniyor...</div> :
       filtered.length === 0 ? (
        <div className="text-center py-16">
          <Hash className="h-12 w-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-400">Henüz kayıtlı IMEI yok</p>
          <p className="text-xs text-slate-300 mt-1">IMEI sorgulayıp "Kaydet" diyebilirsiniz</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(r => (
            <div key={r.id} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all" data-testid={`imei-kayit-${r.id}`}>
              {editId === r.id ? (
                <div className="space-y-3">
                  <div className="font-mono text-sm text-slate-600">{r.imei}</div>
                  <input type="text" value={editIsim} onChange={e => setEditIsim(e.target.value)}
                    placeholder="İsim"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-emerald-400 outline-none" />
                  <textarea value={editNot} onChange={e => setEditNot(e.target.value)}
                    placeholder="Not"
                    rows={2}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-emerald-400 outline-none resize-none" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveEdit(r.id)} className="bg-emerald-500 hover:bg-emerald-600">
                      <Save className="h-3.5 w-3.5 mr-1.5" /> Kaydet
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancelEdit}>İptal</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className={`p-2.5 rounded-xl flex-shrink-0 ${r.valid ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    {r.valid ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 text-red-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-800">{r.isim || 'İsimsiz'}</span>
                      {r.valid ? (
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">KAYITLI</span>
                      ) : (
                        <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">GEÇERSİZ</span>
                      )}
                    </div>
                    <div className="font-mono text-xs text-slate-400 mt-1">IMEI: {r.imei}</div>
                    {r.marka_model && r.marka_model !== '-' && (
                      <div className="text-xs text-slate-500 mt-1">{r.marka_model}</div>
                    )}
                    {r.not_alan && (
                      <p className="text-xs text-slate-500 mt-1.5 bg-slate-50 rounded-lg px-3 py-1.5">{r.not_alan}</p>
                    )}
                    <div className="text-[10px] text-slate-300 mt-1.5">{r.sorgu_tarihi}</div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button onClick={() => startEdit(r)} className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl" title="Düzenle">
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(r.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl" title="Sil">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
